import { Container, Box, BoxProps } from "@chakra-ui/react";
import { FC, useEffect, useRef, useState } from "react";
import { graphql, useStaticQuery } from "gatsby";
import { useInView } from "react-intersection-observer";
import SiteFooter from "src/components/SiteFooter";
import SiteNavbar from "src/components/SiteNavbar";
import { useLocation } from "@reach/router";
import { ShiIcon } from "src/atoms";
import { ESCDA2026Banner } from "src/components/SiteNavbar/components/ESCDA2026Banner";
import { isMigrationPage } from "src/lib/isMigrationPage";
import { SegmentProvider } from "src/components/SegmentContext";
import { SegmentToggle } from "src/components/SegmentToggle";
import GlobalNavigation from "src/components/GlobalNavigation";
import { Slice } from "src/slices/types";
import { NavbarVersion } from "src/components/SiteNavbar/types";



const PageLayout: FC<
  BoxProps & {
    footer?: Gatsby.Maybe<Gatsby.SiteFooterFragment>;
    navbarVersion?: NavbarVersion;
    firstSlice?: Gatsby.Maybe<Slice>;
    startAnchorId?: string;
    isAccounting?: boolean;
  }
> = ({
  children,
  footer,
  navbarVersion,
  firstSlice,
  startAnchorId,
  isAccounting,
  ...props
}) => {
  /**
   * Mobile apps display the privacy page as a WebView,
   * which is the reason why the GET param allows a minimal layout.
   */
  const { pathname, search } = useLocation();
  const searchParams = new URLSearchParams(search);
  const hasMinLayout = searchParams.get("min-layout") === "true";
  const isPrivacyPage = pathname === "/privacy/fr/";
  const shouldMinimizeLayout = isPrivacyPage && hasMinLayout;
  const isMigration = isMigrationPage(pathname);
  const isFull = !shouldMinimizeLayout;

  const data =
    useStaticQuery<Gatsby.PageLayoutGlobalSettingsQueryQuery>(graphql`
      query PageLayoutGlobalSettingsQuery {
        prismicGlobalSettings {
          data {
            account_segment_navbar_logo {
              url
              alt
            }
            account_segment_footer_logo {
              url
              alt
            }
            segments {
              segment_id
              segment_label
              url_prefix
            }
            hide_prefix_paths {
              path
            }
          }
        }
      }
    `);

  const globalSettings = data.prismicGlobalSettings?.data;

  // Safely map Gatsby's readonly/Maybe GraphQL types to our strict UI interfaces
  const segments = (globalSettings?.segments || []).reduce((acc, seg) => {
    if (seg && seg.segment_id) {
      acc.push({
        segment_id: seg.segment_id,
        segment_label: seg.segment_label || "",
        url_prefix: seg.url_prefix || "",
      });
    }
    return acc;
  }, [] as import("src/components/SegmentContext").GlobalSettingsSegment[]);

  const hidePrefixes = (globalSettings?.hide_prefix_paths || []).reduce(
    (acc, p) => {
      if (p && p.path) {
        acc.push({ path: p.path });
      }
      return acc;
    },
    [] as import("src/components/SegmentContext").HidePrefixPath[],
  );
  
    let matchedSegment = "business"; // fallback
  const sortedSegments = [...segments].sort(
    (a, b) => (b.url_prefix?.length || 0) - (a.url_prefix?.length || 0),
  );

  for (const seg of sortedSegments) {
    let prefix = seg.url_prefix;
    if (prefix && !prefix.startsWith("/")) prefix = `/${prefix}`;

    if (prefix && prefix !== "/" && pathname?.startsWith(prefix)) {
      matchedSegment = seg.segment_id;
      break;
    }
  }

  if (matchedSegment === "business") {
    // If no explicit long prefix matched, check if we match the base "/" prefix segment
    const baseSegment = segments.find((s) => s.url_prefix === "/");
    if (baseSegment) matchedSegment = baseSegment.segment_id;
  }

  // Determine which navigation to render
  let finalNavType: "legacy" | "new-business" | "new-accountant" = "legacy";
  let legacyVersion: NavbarVersion = "v2";

  if (isAccounting) {
    // Hardcoded logic for experts-comptables via tags
    finalNavType = "legacy";
    legacyVersion = "v5";
  } else {
    // For "Page", default is global-business-nav if none provided
    if (!navbarVersion || navbarVersion === "global-business-nav") {
      finalNavType = "new-business";
    } else if (navbarVersion === "global-accountant-nav") {
      finalNavType = "new-accountant";
    } else {
      finalNavType = "legacy";
      legacyVersion = navbarVersion as NavbarVersion;
    }
  }

  const topElementsRef = useRef<HTMLDivElement>(null);
  const [topHeight, setTopHeight] = useState(0);

  useEffect(() => {
    const updateHeight = () => {
      if (topElementsRef.current) {
        setTopHeight(topElementsRef.current.offsetHeight);
      }
    };

    updateHeight();
    window.addEventListener("resize", updateHeight);
    return () => window.removeEventListener("resize", updateHeight);
  }, []);

  const { ref: scrollDetectorRef, inView: isDetectorInView } = useInView({
    initialInView: true,
  });

  // Snap when the detector scrolls out of the viewport
  const isScrolled = !isDetectorInView;

  return (
    <SegmentProvider segments={segments} hidePrefixes={hidePrefixes}>
      <Box position="relative" {...props}>
        {/* Dummy element to track scroll position via Intersection Observer */}
        <Box
          ref={scrollDetectorRef}
          position="absolute"
          top={`${topHeight + 20}px`}
          left={0}
          w="1px"
          h="1px"
          pointerEvents="none"
          visibility="hidden"
        />

        {shouldMinimizeLayout ? (
          <Box position="sticky" top={0} zIndex="navbar" bg="white" w="full">
            <ShiIcon
              display="block"
              margin="0 auto"
              name="ShineLogo"
              boxSize="135px"
            />
          </Box>
        ) : isMigration ? (
          <Container
            as="nav"
            size="block"
            display="flex"
            alignItems={{ base: "center", navbar: "flex-start" }}
            justifyContent="space-between"
            paddingY={{ base: "space-16", navbar: "space-8" }}
            paddingX={{ base: "space-16", navbar: "space-58", "2xl": 0 }}
            borderBottom={{ base: "0px solid", navbar: "none" }}
            borderColor="grey-0"
            gridGap={{ base: "space-16", navbar: "0" }}
          >
            <ShiIcon name="ShineLogo" width="135px" height="70px" />
          </Container>
        ) : (
          <Box
            position="sticky"
            top={isScrolled ? `-${topHeight}px` : "0"}
            transition="top 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
            zIndex="navbar"
            bg="white"
            w="full"
          >
            <Box ref={topElementsRef}>
              <ESCDA2026Banner pathname={pathname} />
              <SegmentToggle />
            </Box>

            {finalNavType === "new-business" && (
              <GlobalNavigation segment="business" isSticky={false} />
            )}
            {finalNavType === "new-accountant" && (
              <GlobalNavigation
                segment="accountant"
                isSticky={false}
                accountSegmentNavbarLogo={
                  globalSettings?.account_segment_navbar_logo
                }
              />
            )}
            {finalNavType === "legacy" && (
              <SiteNavbar
                version={legacyVersion as NavbarVersion}
                isSticky={false}
                accountSegmentNavbarLogo={
                  globalSettings?.account_segment_navbar_logo
                }
                firstSlice={firstSlice}
                startAnchorId={startAnchorId}
              />
            )}
          </Box>
        )}

        {children}

        <SiteFooter
          isFull={isFull}
          override={footer}
          accountSegmentFooterLogo={globalSettings?.account_segment_footer_logo}
        />
      </Box>
    </SegmentProvider>
  );
};

export default PageLayout;
